import { mergeCallbackOptionsWithPick } from "@captions-web/utils/merge-callbacks";
import {
  DefaultError,
  InfiniteData,
  infiniteQueryOptions,
  InvalidateOptions,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  skipToken,
  Updater,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";

import { extractVariables, makePaginatedQueryFetcher } from "../../default-fetcher";
import {
  CallerCombinedVariablesForPagination,
  ExtractPaginatedItemTypeWithKey,
  KeysWithPaginatedItems,
  QueryDefinitionOptions,
  QueryFetcherOptions,
  QueryPaginationParams,
} from "../types";

import { mergeQueryKeysWithParams } from "./base-query";

interface QueryPaginationDefinitionOptions<
  ParamsSchema extends z.ZodTypeAny,
  ParamsForPagination extends Partial<z.infer<ParamsSchema>>,
  TransformedResult,
  PageParam,
> {
  getNextPageParam: (
    lastPage: TransformedResult,
    allPages: TransformedResult[],
    lastPageParam: PageParam,
    allPageParams: PageParam[]
  ) => PageParam;

  initialPageParam?: PageParam;

  mapPaginationParamsToQueryParams: (
    paginationParams: QueryPaginationParams<PageParam>
  ) => ParamsForPagination;
}

interface CustomUseInfiniteQueryCallerOptions<
  TQueryKey extends QueryKey,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult,
  PageParam,
> extends Omit<QueryFetcherOptions<TransformedResult>, "onSuccess" | "onError">,
    Omit<
      UseInfiniteQueryOptions<
        z.infer<ResponseSchema>,
        DefaultError,
        InfiniteData<TransformedResult, PageParam>,
        TransformedResult,
        TQueryKey,
        PageParam
      >,
      "getNextPageParam" | "queryKey" | "initialPageParam" | "initialData"
    > {}

export function createQueryInfiniteMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object,
>(
  originalDefinitionOptions: QueryDefinitionOptions<
    TQueryKey,
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  return {
    withPagination<ParamsForPagination extends Partial<z.infer<ParamsSchema>>, PageParam>(
      paginationOptions: QueryPaginationDefinitionOptions<
        ParamsSchema,
        ParamsForPagination,
        TransformedResult,
        PageParam
      >
    ) {
      function makeFetcher(
        variables:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | Record<string, never>,
        callerOptions: QueryFetcherOptions<TransformedResult> = {}
      ) {
        const { onError, onSuccess } = mergeCallbackOptionsWithPick(
          ["onError", "onSuccess"],
          originalDefinitionOptions,
          callerOptions
        );

        return makePaginatedQueryFetcher({
          ...originalDefinitionOptions,
          ...callerOptions,
          headers: {
            ...originalDefinitionOptions.headers,
            ...callerOptions.headers,
          },
          onError,
          onSuccess,
          variables,
          mapPaginationParamsToQueryParams: paginationOptions.mapPaginationParamsToQueryParams,
        });
      }

      function queryOptions(
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        callerOptions: CustomUseInfiniteQueryCallerOptions<
          TQueryKey,
          ResponseSchema,
          TransformedResult,
          PageParam
        > = {}
      ) {
        const variables = combinedParams === skipToken ? {} : combinedParams;

        return infiniteQueryOptions({
          queryKey: mergeQueryKeysWithParams(
            originalDefinitionOptions.queryKey,
            extractVariables(variables)
          ),
          queryFn: makeFetcher(variables, callerOptions),

          getNextPageParam: paginationOptions.getNextPageParam,
          initialPageParam: (paginationOptions.initialPageParam ?? null) as PageParam,

          enabled: combinedParams !== skipToken,
          ...callerOptions,
        });
      }

      function useInfiniteQueryHook(
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        callerOptions: CustomUseInfiniteQueryCallerOptions<
          TQueryKey,
          ResponseSchema,
          TransformedResult,
          PageParam
        > = {}
      ) {
        return useInfiniteQuery(queryOptions(combinedParams, callerOptions));
      }

      async function invalidateQuery(
        queryClient: QueryClient,
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
        options?: InvalidateOptions
      ) {
        const { queryKey } = queryOptions(combinedParams);
        await queryClient.invalidateQueries({ queryKey, exact: true, ...filters }, options);
      }

      async function invalidateAllQueries(
        queryClient: QueryClient,
        filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
        options?: InvalidateOptions
      ) {
        await queryClient.invalidateQueries(
          { queryKey: originalDefinitionOptions.queryKey, exact: false, ...filters },
          options
        );
      }

      function setQueryData(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        updater: Updater<
          InfiniteData<TransformedResult> | undefined,
          InfiniteData<TransformedResult> | undefined
        >
      ) {
        const { queryKey } = queryOptions(combinedParams);
        queryClient.setQueryData<InfiniteData<TransformedResult>>(queryKey, updater);
      }

      function updateItemsInQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        updateItem: (
          data: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>
        ) => ExtractPaginatedItemTypeWithKey<TransformedResult, Key>
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              [pageKey]: {
                ...page[pageKey],
                page: (page[pageKey] as { page: any[] }).page.map(updateItem),
              },
            })),
          };
        });
      }

      function insertItemInQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        newItem: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>,
        position: "start" | "end"
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page, pageIdx) => ({
              ...page,
              [pageKey]: {
                ...page[pageKey],
                page: (page[pageKey] as { page: any[] }).page.flatMap((item, itemIdx) => {
                  if (pageIdx === 0 && itemIdx === 0 && position === "start") {
                    return [newItem, item];
                  }

                  if (
                    pageIdx === data.pages.length - 1 &&
                    itemIdx === item.length - 1 &&
                    position === "end"
                  ) {
                    return [item, newItem];
                  }

                  return [item];
                }),
              },
            })),
          };
        });
      }

      function removeItemFromQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        matchItem: (item: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>) => boolean
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              [pageKey]: {
                ...page[pageKey],
                page: (page[pageKey] as { page: any[] }).page.filter((item) => !matchItem(item)),
              },
            })),
          };
        });
      }

      return {
        queryOptions,
        useInfiniteQuery: useInfiniteQueryHook,
        invalidateQuery,
        invalidateAllQueries,
        setQueryData,
        updateItemsInQueryData,
        removeItemFromQueryData,
        insertItemInQueryData,
      };
    },
  };
}
